import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { QuickAddDialog } from "./QuickAddDialog";
import { CountryDialog } from "./CountryDialog";
import { ContactDialog } from "./ContactDialog";
import { AiCombo } from "../../components/AiCombo";
import type { CrudField } from "./moduleCrud";

interface Option {
  id: string;
  name: string;
}

type QuickAddSpec = NonNullable<CrudField["quickAdd"]>;

interface Props {
  /** CREATE mode — POST endpoint that creates the prospect (/api/v1/clients/lookups/source-leads). */
  createUrl?: string;
  /** CREATE mode — the parent Source Lead select's optionsUrl, re-read after create to pick up the
   *  fresh {id, name} and select it. */
  optionsUrl?: string;
  onAdded?: (options: Option[], selectedId: string) => void;
  /** EDIT mode (Unfollowed Prospects ✎) — the prospect Oid to load + save. */
  editId?: string;
  /** EDIT mode — GET `${getUrl}/${editId}` to prefill, PUT `${updateUrl}/${editId}` to save. */
  getUrl?: string;
  updateUrl?: string;
  onSaved?: () => void;
  onClose: () => void;
}

type FieldType = "text" | "select" | "date" | "number";
interface FieldDef {
  key: string;        // = the create-API payload key (ClientSourceLeadRequest)
  label: string;
  type: FieldType;
  required?: boolean;
  /** For a select: the options endpoint (loaded on open). */
  src?: string;
  /** For a select: static options (Rating / Status enums). */
  options?: Option[];
  /** A welded "+" quick-add popup for the lookup (Topic / Category / Region) — the legacy
   *  Open Prospect "+" on these fields (owner request 2026-10-23, images 853-855). */
  quickAdd?: QuickAddSpec;
  /** The Country "+" opens the full Country master popup (CountryDialog), image 856. */
  quickAddCountry?: boolean;
  /** The Primary Contact "+" opens the full Contact master popup (ContactDialog), image 857. */
  quickAddContact?: boolean;
}
// null = an empty grid cell so the 2-column rows line up like the legacy form.
type Cell = FieldDef | null;

const RATING: Option[] = [{ id: "0", name: "Hot" }, { id: "1", name: "Warm" }, { id: "2", name: "Cold" }];
const STATUS: Option[] = [{ id: "0", name: "None" }, { id: "1", name: "Won" }, { id: "2", name: "Cancelled" }];

// The legacy "Open Prospect" DetailView, section by section. Each section is a flat list
// of cells rendered in a 2-column, row-major grid; the order interleaves the legacy left /
// right columns so each row reads the same as the old form. Field `key` = the payload key.
const SECTIONS: { title: string; cells: Cell[] }[] = [
  {
    title: "Summary",
    cells: [
      { key: "topicId", label: "Topic", type: "select", required: true, src: "/api/v1/clients/lookups/topics",
        quickAdd: { createUrl: "/api/v1/clients/lookups/topics", title: "Topic", icon: "ti-list-details",
          fields: [{ key: "topic", label: "Topic", type: "textarea", required: true, requiredMessage: "Topic is required." }] } },
      { key: "webSite", label: "Web Site", type: "text" },
      { key: "prospects", label: "Prospects", type: "text", required: true },
      { key: "industryId", label: "Industry", type: "select", src: "/api/v1/customers/industries" },
      { key: "clientCode", label: "Client Code", type: "text" },
      { key: "demoDate", label: "Demo Date", type: "date" },
      { key: "account", label: "Account", type: "text" },
      { key: "quoteDate", label: "Quote Date", type: "date" },
      { key: "categoryId", label: "Category", type: "select", src: "/api/v1/customers/categories",
        quickAdd: { createUrl: "/api/v1/clients/lookups/categories", title: "Category", icon: "ti-category",
          fields: [{ key: "category", label: "Category", type: "text", required: true, requiredMessage: "Category is required." },
                   { key: "description", label: "Description", type: "textarea" }] } },
      { key: "probability", label: "Probability", type: "number" },
      { key: "clientNumber", label: "Client Number", type: "text" },
      { key: "rating", label: "Rating", type: "select", options: RATING },
      { key: "fax", label: "Fax", type: "text" },
      { key: "followUpDate", label: "Follow Up Date", type: "date" },
    ],
  },
  {
    title: "Address",
    cells: [
      { key: "street1", label: "Address 1", type: "text" },
      { key: "countryId", label: "Country", type: "select", src: "/api/v1/customers/countries", quickAddCountry: true },
      { key: "street2", label: "Address 2", type: "text" },
      { key: "stateId", label: "State", type: "select", src: "/api/v1/customers/states" },
      { key: "regionId", label: "Region", type: "select", src: "/api/v1/clients/lookups/regions",
        quickAdd: { createUrl: "/api/v1/clients/lookups/regions", title: "Region", icon: "ti-map",
          fields: [{ key: "name", label: "Region Name", type: "text", required: true, requiredMessage: "Region Name is required." },
                   { key: "description", label: "Description", type: "textarea" }] } },
      { key: "cityId", label: "City", type: "select", src: "/api/v1/customers/cities" },
      { key: "zip", label: "Zip", type: "text" },
      null,
    ],
  },
  {
    title: "Contact",
    cells: [
      { key: "primaryContactId", label: "Primary Contact", type: "select", src: "/api/v1/customers/contacts", quickAddContact: true },
      { key: "otherPhone", label: "Other Phone", type: "text" },
      { key: "officePhone", label: "Office Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "mobilePhone", label: "Cell Phone", type: "text" },
      null,
    ],
  },
  {
    title: "General",
    cells: [
      { key: "ownerId", label: "Owner", type: "select", src: "/api/v1/customers/owners" },
      { key: "remark", label: "Remark", type: "text" },
      { key: "status", label: "Status", type: "select", options: STATUS },
      null,
    ],
  },
];

const ALL_FIELDS: FieldDef[] = SECTIONS.flatMap((s) => s.cells.filter((c): c is FieldDef => c != null));

/**
 * The legacy CRM "Open Prospect" form, opened from the Unfollowed Clients ▸ Source Lead "+"
 * (owner request 2026-10-23). Creates a CRM Prospect via the existing
 * POST /api/v1/clients/lookups/source-leads, then re-reads the parent Source Lead options and
 * selects the new row. Sections / fields / lookups mirror the legacy Open Prospect DetailView.
 * The Quotes / Notes child grids (empty on a brand-new prospect) are not part of this create
 * dialog.
 */
export function OpenProspectDialog({ createUrl, optionsUrl, onClose, onAdded, editId, getUrl, updateUrl, onSaved }: Props) {
  const notify = useNotify();
  const [values, setValues] = useState<Record<string, string>>(() => ({ probability: "0", rating: "0", status: "0" }));
  const [opts, setOpts] = useState<Record<string, Option[]>>({});
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  // The lookup "+" popups: Topic / Category / Region open QuickAddDialog; Country opens CountryDialog.
  const [qaField, setQaField] = useState<FieldDef | null>(null);
  const [countryField, setCountryField] = useState<FieldDef | null>(null);
  const [contactField, setContactField] = useState<FieldDef | null>(null);
  // Quotes | Notes child grids (legacy Open Prospect bottom tabs). Empty on a NEW prospect,
  // exactly as the old form shows (Count=0). Quotes is display-only (a Quote is the separate
  // "Open Quotes" entity); Notes are buffered here and created under the prospect on save.
  const [tab, setTab] = useState<"quotes" | "notes">("quotes");
  const [notes, setNotes] = useState<{ title: string; text: string }[]>([]);
  const addNote = () => setNotes((n) => [...n, { title: "", text: "" }]);
  const setNote = (i: number, patch: Partial<{ title: string; text: string }>) =>
    setNotes((n) => n.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeNote = (i: number) => setNotes((n) => n.filter((_, j) => j !== i));

  // Load every lookup's options once on open (distinct endpoints, in parallel).
  useEffect(() => {
    const urls = Array.from(new Set(ALL_FIELDS.map((f) => f.src).filter((u): u is string => !!u)));
    let alive = true;
    void Promise.all(
      urls.map(async (u) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(u, { silentLoading: true } as never);
          return [u, data?.data ?? []] as const;
        } catch {
          return [u, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => {
      if (alive) setOpts(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
  }, []);

  // EDIT mode: load the prospect and prefill the form (FK fields come back as Oids → the selects).
  useEffect(() => {
    if (!editId || !getUrl) return;
    let alive = true;
    void api.get<{ data?: Record<string, unknown> }>(`${getUrl}/${editId}`).then(({ data }) => {
      if (!alive || !data?.data) return;
      const d = data.data;
      const next: Record<string, string> = {};
      for (const f of ALL_FIELDS) {
        const v = d[f.key];
        if (v == null) continue;
        next[f.key] = f.type === "date" ? String(v).slice(0, 10) : String(v);
      }
      setValues((s) => ({ ...s, ...next }));
    });
    return () => { alive = false; };
  }, [editId, getUrl]);

  const missingRequired = useMemo(
    () => ALL_FIELDS.some((f) => f.required && !values[f.key]?.trim()),
    [values],
  );

  const set = (k: string, v: string) => setValues((p) => ({ ...p, [k]: v }));

  // After a "+" popup creates a lookup value, replace that field's options with the fresh list and
  // select the new row (the popups re-read the field's own `src`, so IDs match).
  const applyAdded = (field: FieldDef, options: Option[], selectedId: string) => {
    if (field.src) setOpts((p) => ({ ...p, [field.src!]: options }));
    if (selectedId) set(field.key, selectedId);
  };

  const submit = async () => {
    setTried(true);
    if (missingRequired) return;
    setSaving(true);
    try {
      // Payload keys == field keys == ClientSourceLeadRequest keys. Empty strings dropped.
      const payload: Record<string, string> = {};
      for (const f of ALL_FIELDS) {
        const v = (values[f.key] ?? "").trim();
        if (v) payload[f.key] = v;
      }
      // EDIT mode: PUT the prospect + any newly-added notes, then close.
      if (editId && updateUrl) {
        await api.put(`${updateUrl}/${editId}`, payload);
        const editNotes = notes.filter((n) => n.title.trim() && n.text.trim());
        if (editNotes.length) {
          await Promise.all(editNotes.map((n) =>
            api.post("/api/v1/crm-notes", { ownerId: editId, title: n.title.trim(), text: n.text.trim() })));
        }
        notify.success("Saved successfully.");
        onSaved?.();
        onClose();
        return;
      }
      const created = await api.post<{ data?: { oid?: string } }>(createUrl ?? "", payload);
      const newId = created.data?.data?.oid ?? "";
      // Create the buffered Notes under the new prospect (scope resolves to "prospect" from the
      // owner id). Quotes are not inline-created here (that is the separate Open Quotes feature).
      const noteRows = notes.filter((n) => n.title.trim() && n.text.trim());
      if (newId && noteRows.length) {
        await Promise.all(
          noteRows.map((n) => api.post("/api/v1/crm-notes", { ownerId: newId, title: n.title.trim(), text: n.text.trim() })),
        );
      }
      const { data } = await api.get<{ data?: Option[] }>(optionsUrl ?? "", { silentLoading: true } as never);
      const fresh = data?.data ?? [];
      // Prefer the server-returned oid; fall back to matching the typed Prospects name.
      const selected =
        (newId && fresh.find((o) => o.id.toLowerCase() === newId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === values.prospects?.trim())?.id ||
        newId;
      notify.success("Saved successfully.");
      onAdded?.(fresh, selected);
      onClose();
    } catch {
      /* the shared axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title="Open Prospect"
      icon="ti-user-plus"
      width={860}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="op-form">
        {SECTIONS.map((sec) => (
          <div className="op-section" key={sec.title}>
            <div className="op-section-h">{sec.title}</div>
            <div className="op-grid">
              {sec.cells.map((cell, i) =>
                cell == null ? (
                  <div key={`sp-${sec.title}-${i}`} />
                ) : (
                  <div className="op-field" key={cell.key}>
                    <label className="op-label" htmlFor={`op-${cell.key}`}>
                      {cell.label}
                      {cell.required ? <> :<span className="req">*</span></> : ":"}
                    </label>
                    {cell.type === "select" ? (
                      cell.quickAdd || cell.quickAddCountry || cell.quickAddContact ? (
                        // Welded "+" (owner report 2026-10-23, image 859): the shared <AiCombo> renders
                        // the "New" button + dropdown caret INSIDE one bordered control, not a separate box.
                        <AiCombo
                          value={values[cell.key] || null}
                          options={(cell.options ?? opts[cell.src ?? ""] ?? []).map((o) => ({ value: o.id, label: o.name }))}
                          onChange={(v) => set(cell.key, v ?? "")}
                          onNew={() =>
                            cell.quickAddContact
                              ? setContactField(cell)
                              : cell.quickAddCountry
                                ? setCountryField(cell)
                                : setQaField(cell)
                          }
                          newTitle={`New ${cell.label}`}
                          placeholder="N/A"
                          naLabel="N/A"
                          className={tried && cell.required && !values[cell.key]?.trim() ? "required-empty" : undefined}
                          compact
                        />
                      ) : (
                        <select
                          id={`op-${cell.key}`}
                          className={`op-input${tried && cell.required && !values[cell.key]?.trim() ? " required-empty" : ""}`}
                          value={values[cell.key] ?? ""}
                          onChange={(e) => set(cell.key, e.target.value)}
                        >
                          <option value="">N/A</option>
                          {(cell.options ?? opts[cell.src ?? ""] ?? []).map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <input
                        id={`op-${cell.key}`}
                        className={`op-input${tried && cell.required && !values[cell.key]?.trim() ? " required-empty" : ""}`}
                        type={cell.type === "date" ? "date" : cell.type === "number" ? "number" : "text"}
                        value={values[cell.key] ?? ""}
                        onChange={(e) => set(cell.key, e.target.value)}
                      />
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        ))}

        {/* Quotes | Notes child grids — legacy Open Prospect bottom tabs. Empty on a new
            prospect (Count=0), as the old form shows. */}
        <div className="op-childwrap">
          <div className="op-tabs">
            <button type="button" className={`op-tab${tab === "quotes" ? " active" : ""}`} onClick={() => setTab("quotes")}>
              Quotes
            </button>
            <button type="button" className={`op-tab${tab === "notes" ? " active" : ""}`} onClick={() => setTab("notes")}>
              Notes
            </button>
          </div>
          {tab === "quotes" ? (
            <>
              <div className="op-childbar">
                <span className="op-cb muted" title="Quotes are created in the Open Quotes screen">New</span>
                <span className="op-cb muted">Link</span>
                <span className="op-cb muted">Unlink</span>
                <span className="op-cb muted">Edit</span>
                <span className="op-cb muted">Delete</span>
                <span className="op-cb muted">Export to</span>
              </div>
              <table className="cd-state-tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>QuoteID</th>
                    <th>Title</th>
                    <th>Amount</th>
                    <th>Expiration Date</th>
                    <th>Status</th>
                    <th>IsGobalDiscount</th>
                    <th>OfficePhone</th>
                    <th>PO#</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={9} className="cd-empty">
                      No data to display
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="op-count">Count = 0</div>
            </>
          ) : (
            <>
              <div className="op-childbar">
                <button type="button" className="op-cb" onClick={addNote} disabled={saving}>
                  <i className="ti ti-plus" aria-hidden="true" /> New
                </button>
                <span className="op-cb muted">Link</span>
                <span className="op-cb muted">Unlink</span>
                <span className="op-cb muted">Edit</span>
                <span className="op-cb muted">Delete</span>
                <span className="op-cb muted">Export to</span>
              </div>
              <table className="cd-state-tbl">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Note</th>
                    <th aria-label="remove" />
                  </tr>
                </thead>
                <tbody>
                  {notes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="cd-empty">
                        No data to display
                      </td>
                    </tr>
                  ) : (
                    notes.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <input className="op-input" value={r.title} onChange={(e) => setNote(i, { title: e.target.value })} />
                        </td>
                        <td>
                          <input className="op-input" value={r.text} onChange={(e) => setNote(i, { text: e.target.value })} />
                        </td>
                        <td className="cd-rm">
                          <button type="button" className="cd-rm-btn" title="Remove" onClick={() => removeNote(i)}>
                            <i className="ti ti-x" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="op-count">Count = {notes.length}</div>
            </>
          )}
        </div>
      </div>

      {/* Lookup "+" popups (Topic / Category / Region, image 853-855) + the Country master popup
          (image 856). On success each re-reads its field's options and selects the new row. */}
      {qaField?.quickAdd && (
        <QuickAddDialog
          spec={qaField.quickAdd}
          optionsUrl={qaField.src ?? ""}
          onClose={() => setQaField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(qaField, options, selectedId);
            setQaField(null);
          }}
        />
      )}
      {countryField && (
        <CountryDialog
          createUrl="/api/v1/system/countries"
          optionsUrl={countryField.src ?? ""}
          onClose={() => setCountryField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(countryField, options, selectedId);
            setCountryField(null);
          }}
        />
      )}
      {contactField && (
        <ContactDialog
          createUrl="/api/v1/clients/contacts"
          optionsUrl={contactField.src ?? ""}
          onClose={() => setContactField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(contactField, options, selectedId);
            setContactField(null);
          }}
        />
      )}
    </AppDialog>
  );
}
